"use client"

import { ReactElement, useCallback, useEffect, useState } from "react"
import { getAnalyticsConfig } from "@modules/analytics/lib/config"
import {
  clearConsentCookie,
  clearVendorCookies,
  readConsentFromDocument,
} from "@modules/analytics/lib/consent"
import type { ConsentDecision } from "@modules/analytics/lib/consent"
import { CONSENT_CHANGED_EVENT } from "@modules/analytics/lib/constants"
import { clearTrackedPurchases } from "@modules/analytics/lib/purchase-dedup"
import {
  hasLoadedTagInDocument,
  resolveTags,
} from "@modules/analytics/lib/tags"

const MANAGE_COOKIES_LABEL = "Gerenciar cookies"

/**
 * Story 10.10 — ponto de entrada PERSISTENTE para rever a decisão de cookies (a LGPD dá o
 * direito de retirar o consentimento, e o cookie da casa dura ~180 dias).
 *
 * A retirada tem de RETIRAR: apagar só o cookie da casa deixava o `gtag.js` no DOM, os cookies
 * do Google vivos por ~2 anos e as marcas de conversão no `localStorage` — consentimento
 * formalmente revogado com o terceiro ainda a identificar o visitante. Por isso apaga tudo, e
 * recarrega quando o tag já estava carregado: injetar um `<Script>` é reversível, desinjetá-lo
 * não é.
 *
 * VISÍVEL exatamente quando existe uma DECISÃO GRAVADA — e não quando há tag configurado. As
 * duas regras divergem nos dois casos que interessam: quem ainda não decidiu já tem o banner à
 * frente e veria aqui um botão sem efeito observável; e quem consentiu antes de o id de env ser
 * removido ficaria com o cookie da casa (180 d) e os do Google (~2 anos) sem NENHUMA forma de
 * os retirar pela loja.
 */
export default function ManageCookiesLink(): ReactElement | null {
  // "unknown" no server e na 1ª renderização do cliente (evita mismatch de hidratação); o
  // efeito lê o cookie real só após montar, no browser. Mesmo padrão do banner.
  const [consent, setConsent] = useState<ConsentDecision>("unknown")

  const syncFromCookie = useCallback((): void => {
    setConsent(readConsentFromDocument())
  }, [])

  useEffect(() => {
    syncFromCookie()
  }, [syncFromCookie])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }
    // O banner emite este evento ao gravar a decisão (e nós ao apagá-la): sem isto o botão só
    // aparecia/desaparecia na navegação seguinte.
    window.addEventListener(CONSENT_CHANGED_EVENT, syncFromCookie)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, syncFromCookie)
  }, [syncFromCookie])

  if (consent === "unknown") {
    return null
  }

  const handleClick = (): void => {
    clearConsentCookie()
    clearVendorCookies()
    clearTrackedPurchases()
    setConsent("unknown")
    const hasLoadedTag = hasLoadedTagInDocument(
      resolveTags(getAnalyticsConfig())
    )
    if (typeof window === "undefined") {
      return
    }
    if (hasLoadedTag) {
      // Única forma de o terceiro sair mesmo da página; ao voltar, o gate está em "unknown",
      // logo nada recarrega e o banner pergunta de novo.
      window.location.reload()
      return
    }
    // Sem tag no DOM basta avisar as cascas irmãs: o banner reaparece na hora, sem navegação.
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT))
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="footer-manage-cookies"
      className="flex min-h-[44px] items-center text-left text-subtle transition-colors hover:text-text"
    >
      {MANAGE_COOKIES_LABEL}
    </button>
  )
}
